import { useMemo, useState } from 'react';
import { CheckOutlined, WarningOutlined } from '@ant-design/icons';

import { Badge, Button, Checkbox, Dialog, Input, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import type { BangBoQua, IntrospectBang } from '../../types';
import { mauCot, tinhBoCot } from '../../nghiep-vu/chon-cot';
import { thieuBangDong } from '../../nghiep-vu/chung-tu-sap';
import {
  boCotBanDau,
  chonBanDau,
  coDuLieu,
  cotRongConGiu,
  cotRongTheoBang,
  cuaKhach,
  demCotMoi,
  gomCanhBao,
  goiLuu,
  locVaXep,
  MOI_LAN,
  NGUONG_NHAC,
  NGUONG_NHIEU_COT,
  nhomBoQua,
  type LocNhanh,
} from '../../nghiep-vu/khai-bang';
import { useBangDaKhai, useHoSoNguon, useLuuBang } from '../../hooks/useKho';
import { useMoTaLoi } from '../../hooks/useMoTaLoi';
import { DaiLoi } from '../chung/VungDuLieu';
import { ChonCot } from './ChonCot';
import { CopyCotDialog } from './CopyCotDialog';

export interface KhaiBangDialogProps {
  sourceId: string;
  /** Lược đồ vừa đọc; `null` = hộp đóng. */
  bang: IntrospectBang[] | null;
  canhBao: string[];
  boQua: BangBoQua[];
  onClose: () => void;
}

/**
 * Chọn bảng để khai, từ lược đồ vừa đọc được ở nguồn (port `KhaiBangDialog.tsx` native).
 *
 * Không còn mặc định chọn hết: đúng ở bốn mươi bảng, sai ở hai nghìn tám (SAP B1: 79% đối tượng rỗng). Dưới
 * `NGUONG_NHAC` chọn hết; vượt thì không chọn gì và mời tìm. `notes` của mỗi bảng là CÂU HỎI (chỗ máy đã đoán khoá chính,
 * cột mốc), nên hiện ngay cạnh tên bảng — đoán sai khoá chính là mỗi chu kỳ nhân đôi số dòng.
 *
 * Thân hộp chỉ dựng khi đã có lược đồ VÀ bản khai cũ, và bị tháo khi đóng (`destroyOnHidden`) — nên lựa chọn ban đầu tính
 * đúng một lần mỗi lần mở, không bị một lần `invalidateQueries` ở đâu đó đặt lại giữa chừng (mất việc mà không có lỗi).
 */
export function KhaiBangDialog(props: KhaiBangDialogProps) {
  const { t } = useT();
  const daKhai = useBangDaKhai(props.sourceId);
  // Hồ sơ đã đo — để biết cột nào rỗng. Hộp đóng thì không hỏi.
  const hoSo = useHoSoNguon(props.sourceId, Boolean(props.bang));

  return (
    <Dialog
      open={props.bang !== null}
      onClose={props.onClose}
      title={t('troLyAiKhoKhaiBang.tieuDe')}
      // Rộng theo màn hình, có trần: 720px ép lưới tên cột xuống ba cột hẹp và cắt mất tên cột SAP.
      width="min(1100px, 92vw)"
      destroyOnHidden
    >
      {props.bang === null ? null : daKhai.error ? (
        <DaiLoi loi={daKhai.error} onThuLai={() => void daKhai.refetch()} />
      ) : daKhai.data === undefined ? (
        // Chưa biết bản khai cũ thì chưa quyết lựa chọn ban đầu.
        <div className="space-y-2" aria-busy="true">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-2/3" />
        </div>
      ) : (
        <ThanHop
          {...props}
          bang={props.bang}
          daKhai={daKhai.data as unknown as { name: string; columns?: { name: string }[] }[]}
          hoSoTables={hoSo.data?.tables ?? []}
        />
      )}
    </Dialog>
  );
}

function ThanHop({
  sourceId,
  bang,
  canhBao,
  boQua,
  onClose,
  daKhai,
  hoSoTables,
}: Omit<KhaiBangDialogProps, 'bang'> & {
  bang: IntrospectBang[];
  daKhai: { name: string; columns?: { name: string }[] }[];
  hoSoTables: Record<string, unknown>[];
}) {
  const { t, locale } = useT();
  const toast = useToast();
  const moTaLoi = useMoTaLoi();
  const luu = useLuuBang(sourceId);

  const ten = useMemo(() => bang.map((b) => b.suggestion.name), [bang]);
  const [chon, setChon] = useState<Set<string>>(() => chonBanDau(ten, daKhai.map((b) => b.name)));
  /** Cột đang BỎ theo bảng. Bảng đã khai thì bỏ sẵn cột không có trong bản khai (trừ cột khoá). */
  const [boCot, setBoCot] = useState<Record<string, Set<string>>>(() => boCotBanDau(bang, daKhai));
  const [tim, setTim] = useState('');
  const [hien, setHien] = useState(MOI_LAN);
  const [moCanhBao, setMoCanhBao] = useState(false);
  /** Bảng đang mở bảng chọn cột — một cái một lúc, mở nhiều thì danh sách trôi mất. */
  const [moCot, setMoCot] = useState<string | null>(null);
  const [copyTu, setCopyTu] = useState<string | null>(null);
  /** Giấu bảng ĐÃ ĐO là rỗng. Mặc định TẮT: giấu sẵn là quyết hộ dựa trên một con số ước lượng. */
  const [anRong, setAnRong] = useState(false);
  const [moNote, setMoNote] = useState<string | null>(null);
  const [moGom, setMoGom] = useState(false);
  /** Lọc nhanh cho lúc CHƯA biết mình tìm gì — lúc một danh sách 2.832 dòng đáng sợ nhất. */
  const [locNhanh, setLocNhanh] = useState<LocNhanh | null>(null);
  const [tien, setTien] = useState<{ xong: number; tong: number } | null>(null);

  const cotMoi = useMemo(() => demCotMoi(bang, daKhai), [bang, daKhai]);
  const cotRong = useMemo(() => cotRongTheoBang(hoSoTables), [hoSoTables]);
  const rongConGiu = useMemo(() => cotRongConGiu(bang, cotRong, boCot), [bang, cotRong, boCot]);
  const soCotRong = rongConGiu.reduce((a, x) => a + x.cot.length, 0);

  const bangTheoTen = useMemo(() => {
    const m: Record<string, IntrospectBang> = {};
    for (const b of bang) m[b.suggestion.name] = b;
    return m;
  }, [bang]);

  const loc = useMemo(() => locVaXep(bang, { tim, anRong, locNhanh, chon }), [bang, tim, anRong, locNhanh, chon]);
  const gomNote = useMemo(() => gomCanhBao(bang), [bang]);
  const soCoDuLieu = useMemo(() => bang.filter(coDuLieu).length, [bang]);
  const soRongCot = useMemo(() => bang.filter((b) => (b.suggestion.columns?.length ?? 0) >= NGUONG_NHIEU_COT).length, [bang]);
  /** Bảng ĐÃ ĐO là rỗng — chỉ thứ đó mới đủ căn cứ để giấu. */
  const soRong = useMemo(() => bang.filter((b) => b.soDong === 0).length, [bang]);
  const nhomLyDo = useMemo(() => nhomBoQua(boQua), [boQua]);
  const tenLoc = useMemo(() => loc.map((b) => b.suggestion.name), [loc]);
  const thieuDong = useMemo(() => thieuBangDong(chon, new Set(ten)), [chon, ten]);
  const nhieuBang = chon.size > NGUONG_NHAC;

  /** Bỏ mọi cột rỗng còn giữ — một THAY ĐỔI vào bản khai sắp gửi, không phải phép ẩn; vẫn mở từng bảng tích lại được. */
  const boHetCotRong = () => {
    setBoCot((cu) => {
      const moi = { ...cu };
      for (const { bang: b, cot } of rongConGiu) moi[b] = new Set([...(moi[b] ?? []), ...cot]);
      return moi;
    });
    toast.success(t('troLyAiKhoKhaiBang.daBoCotRong', { n: soVN(soCotRong, locale) }));
  };

  /** Áp bộ cột của `nguon` lên các bảng đích, và CHỌN LUÔN chúng để khai — không thì lời "lấy đúng bộ cột này" không dẫn tới đâu. */
  const apDungCot = (nguon: string, dich: string[]) => {
    const goc = bangTheoTen[nguon];
    if (!goc) return;
    const mau = mauCot(goc.suggestion, boCot[nguon] ?? new Set());
    setBoCot((cu) => {
      const n = { ...cu };
      for (const x of dich) {
        const b = bangTheoTen[x];
        if (b) n[x] = tinhBoCot(mau, b.suggestion);
      }
      return n;
    });
    setChon((cu) => new Set([...cu, ...dich]));
    toast.success(t('troLyAiKhoKhaiBang.copyXong', { n: soVN(dich.length, locale), m: soVN(mau.size, locale) }));
  };

  const bat = (name: string) =>
    setChon((cu) => {
      const moi = new Set(cu);
      if (moi.has(name)) moi.delete(name);
      else moi.add(name);
      return moi;
    });
  // Hai nút áp lên KẾT QUẢ ĐANG LỌC: gõ `A`, bỏ hết — thay vì bấm 341 lần.
  const chonHetLoc = () => setChon((cu) => new Set([...cu, ...tenLoc]));
  const boHetLoc = () =>
    setChon((cu) => {
      const moi = new Set(cu);
      for (const x of tenLoc) moi.delete(x);
      return moi;
    });

  const ghi = () =>
    luu.mutate(
      {
        tables: goiLuu(bang, chon, boCot),
        // Gửi ĐỀ XUẤT dò được: giữ lời khai nghiệp vụ cũ, không thì mỗi lần nguồn thêm một cột là phải khai lại từ đầu.
        giuLoiKhai: true,
        onTien: (xong, tong) => setTien({ xong, tong }),
      },
      {
        onSettled: () => setTien(null),
        onSuccess: (r) => {
          toast.success(t('troLyAiKhoKhaiBang.daKhai', { n: soVN(r.saved, locale), m: soVN(r.created, locale) }));
          onClose();
        },
        onError: (e) => toast.error(t('troLyAiKhoKhaiBang.khongKhaiDuoc', { loi: moTaLoi(e) })),
      },
    );

  return (
    <div className="space-y-3">
      <p className="text-xs leading-relaxed text-content-secondary">{t('troLyAiKhoKhaiBang.moTa')}</p>

      {/* Bảng bị loại gộp theo LÝ DO — một dòng đếm, mở ra mới thấy tên (SAP B1: 218 câu nối nhau cao hơn danh sách bảng). */}
      {nhomLyDo.length ? (
        <div className="rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-700">
          <div className="flex items-start gap-2">
            <WarningOutlined className="mt-0.5 shrink-0" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="font-medium">{t('troLyAiKhoKhaiBang.boQuaTong', { n: soVN(boQua.length, locale) })}</p>
              <ul className="mt-1 space-y-0.5">
                {nhomLyDo.map(([ma, ds]) => (
                  <li key={ma}>
                    {t(`troLyAiKhoKhaiBang.lyDo.${ma}`, { n: soVN(ds.length, locale) })}
                    {moCanhBao ? (
                      <span className="block break-words font-mono text-content-muted">{ds.join(', ')}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
            <button
              type="button"
              aria-expanded={moCanhBao}
              className="shrink-0 underline underline-offset-2"
              onClick={() => setMoCanhBao((v) => !v)}
            >
              {moCanhBao ? t('troLyAiKhoKhaiBang.thuGon') : t('troLyAiKhoKhaiBang.xemTen')}
            </button>
          </div>
        </div>
      ) : canhBao.length ? (
        <div className="flex items-start gap-2 rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-700">
          <WarningOutlined className="mt-0.5 shrink-0" aria-hidden />
          <span>{t('troLyAiKhoKhaiBang.canhBao', { ds: canhBao.join(' · ') })}</span>
        </div>
      ) : null}

      <Input
        value={tim}
        onChange={(e) => {
          setTim(e.target.value);
          setHien(MOI_LAN);
        }}
        placeholder={t('troLyAiKhoKhaiBang.timPlaceholder')}
        aria-label={t('troLyAiKhoKhaiBang.timPlaceholder')}
        allowClear
      />

      {/* Mỗi nút lọc mang SỐ của chính nó; nút 0 kết quả khoá chứ không ẩn — ẩn thì hàng nút nhảy chỗ mỗi lần gõ. */}
      <div className="flex flex-wrap items-center gap-1.5 text-xs" role="group">
        {(
          [
            ['co_dulieu', soCoDuLieu],
            ['da_khai', chon.size],
            ['rong_cot', soRongCot],
          ] as const
        ).map(([ma, n]) => {
          const dang = locNhanh === ma;
          return (
            <button
              key={ma}
              type="button"
              disabled={!n}
              aria-pressed={dang}
              onClick={() => setLocNhanh(dang ? null : ma)}
              className={cn(
                'rounded-full px-2.5 py-1 transition-colors disabled:cursor-not-allowed disabled:text-content-muted',
                dang ? 'bg-primary-600 text-white' : 'bg-subtle text-content-secondary hover:bg-primary-50',
              )}
            >
              {t(`troLyAiKhoKhaiBang.locNhanh.${ma}`, { n: soVN(n, locale) })}
            </button>
          );
        })}
      </div>

      {soRong > 0 ? (
        <Checkbox checked={anRong} onChange={(e) => setAnRong(e.target.checked)}>
          <span className="text-xs text-content-secondary">
            {t('troLyAiKhoKhaiBang.anBangRong', { n: soVN(soRong, locale) })}
          </span>
        </Checkbox>
      ) : null}

      {/* Bỏ cột rỗng: cùng nguồn sự thật với công tắc trên, một mức sâu hơn — gói ngữ cảnh ~50 token MỖI cột MỖI lượt hỏi. */}
      {soCotRong > 0 ? (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Button size="sm" variant="ghost" onClick={boHetCotRong}>
            {t('troLyAiKhoKhaiBang.boCotRong', { n: soVN(soCotRong, locale) })}
          </Button>
          <span className="text-content-muted">{t('troLyAiKhoKhaiBang.boCotRongMo')}</span>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={cn('text-xs', nhieuBang ? 'text-warning-700' : 'text-content-secondary')}>
          {t('troLyAiKhoKhaiBang.daChon', { n: soVN(chon.size, locale), m: soVN(ten.length, locale) })}
          {tim ? ` · ${t('troLyAiKhoKhaiBang.dangLoc', { n: soVN(loc.length, locale) })}` : ''}
        </span>
        <div className="flex gap-1.5">
          <Button size="sm" variant="ghost" onClick={chonHetLoc}>
            {tim ? t('troLyAiKhoKhaiBang.chonHetLoc', { n: soVN(loc.length, locale) }) : t('troLyAiKhoKhaiBang.chonHet')}
          </Button>
          <Button size="sm" variant="ghost" onClick={boHetLoc}>
            {tim ? t('troLyAiKhoKhaiBang.boHetLoc', { n: soVN(loc.length, locale) }) : t('troLyAiKhoKhaiBang.boHet')}
          </Button>
        </div>
      </div>

      {/* NHẮC, không chặn: người vận hành biết lược đồ của mình rõ hơn một hằng số. */}
      {nhieuBang ? (
        <p className="rounded-lg bg-warning-50 px-3 py-2 text-xs leading-relaxed text-warning-700">
          {t('troLyAiKhoKhaiBang.nhieuBang', { n: soVN(chon.size, locale) })}
        </p>
      ) : null}

      <div className="space-y-1.5">
        {/* Cảnh báo gom MỘT LẦN cho cả danh sách, gập mặc định — câu chung nói một lần, câu hiếm nổi bật lên. */}
        {gomNote.length ? (
          <div className="rounded-lg border border-warning-100 bg-warning-50">
            <button
              type="button"
              aria-expanded={moGom}
              onClick={() => setMoGom((v) => !v)}
              className="flex w-full items-center gap-2 px-3 py-2 text-start text-xs text-warning-700"
            >
              <WarningOutlined aria-hidden />
              <span className="flex-1">
                {t('troLyAiKhoKhaiBang.gomCanhBao', { n: soVN(gomNote.length, locale), m: soVN(gomNote[0]?.n ?? 0, locale) })}
              </span>
              <span className="underline decoration-dotted">
                {moGom ? t('troLyAiKhoKhaiBang.thuGon') : t('troLyAiKhoKhaiBang.xemChiTiet')}
              </span>
            </button>
            {moGom ? (
              <ul className="space-y-1.5 border-t border-warning-100 px-3 py-2">
                {gomNote.map((g) => (
                  <li key={g.mau} className="text-xs leading-relaxed text-warning-700">
                    <span className="font-medium">{t('troLyAiKhoKhaiBang.nBang', { n: soVN(g.n, locale) })}</span> · {g.mau}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {loc.length === 0 ? (
          <p className="py-6 text-center text-xs text-content-muted">{t('troLyAiKhoKhaiBang.timKhongThay')}</p>
        ) : null}
        {loc.slice(0, hien).map((b) => {
          const s = b.suggestion;
          const on = chon.has(s.name);
          const tongCot = s.columns?.length ?? 0;
          const daBo = boCot[s.name]?.size ?? 0;
          return (
            <div
              key={s.name}
              className={cn(
                'rounded-lg border transition-colors',
                on ? 'border-primary-200 bg-primary-50' : 'border-default bg-surface',
              )}
            >
              <div className="flex items-start gap-2">
                <button
                  type="button"
                  aria-pressed={on}
                  onClick={() => bat(s.name)}
                  className="flex min-w-0 flex-1 items-start gap-2.5 rounded-lg px-3 py-2.5 text-start"
                >
                  <span
                    aria-hidden
                    className={cn(
                      'mt-0.5 flex size-[18px] shrink-0 items-center justify-center rounded-sm border text-xs text-white',
                      on ? 'border-primary-600 bg-primary-600' : 'border-default bg-surface',
                    )}
                  >
                    {on ? <CheckOutlined className="text-2xs" /> : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs font-medium text-content-primary">{s.name}</span>
                      {s.title ? <span className="text-xs text-content-muted">{s.title}</span> : null}
                      {/* Số dòng TRƯỚC mọi huy hiệu: ở ERP đóng gói, "có dữ liệu không" là câu hỏi quyết định. */}
                      {b.soDong === 0 ? (
                        <Badge variant="neutral">{t('troLyAiKhoKhaiBang.bangRong')}</Badge>
                      ) : typeof b.soDong === 'number' ? (
                        <Badge variant="primary">{t('troLyAiKhoKhaiBang.soDong', { n: soVN(b.soDong, locale) })}</Badge>
                      ) : null}
                      {/* Số CỘT cạnh số DÒNG: ở SAP B1 số cột mới là cái đắt (~50 token mỗi cột mỗi lượt hỏi). */}
                      <Badge variant={tongCot >= NGUONG_NHIEU_COT ? 'warning' : 'neutral'}>
                        {t('troLyAiKhoKhaiBang.soCot', { n: soVN(tongCot, locale) })}
                      </Badge>
                      {cuaKhach(b) ? <Badge variant="teal">{t('troLyAiKhoKhaiBang.bangCuaKhach')}</Badge> : null}
                      {b.supportsIncremental ? (
                        <Badge variant="success">{t('troLyAiKhoKhaiBang.tangDan')}</Badge>
                      ) : (
                        <Badge variant="warning">{t('troLyAiKhoKhaiBang.chepDay')}</Badge>
                      )}
                      {/* Cột mới bị BỎ SẴN (xem `boCotBanDau`) — nói ra, không thì nó là thay đổi lược đồ lặng lẽ. */}
                      {cotMoi.has(s.name) ? (
                        <Badge variant="primary">
                          {t('troLyAiKhoKhaiBang.cotMoi', { n: soVN(cotMoi.get(s.name) ?? 0, locale) })}
                        </Badge>
                      ) : null}
                    </span>
                    {/* Khoá chính xuống dòng phụ, KHÔNG bỏ: đoán sai là mỗi chu kỳ nhân đôi số dòng. */}
                    <span className="mt-0.5 block truncate font-mono text-2xs text-content-muted">
                      {t('troLyAiKhoKhaiBang.khoaChinh', { ds: (s.primaryKey ?? []).join(', ') || '—' })}
                    </span>
                  </span>
                </button>
                {b.notes.length ? (
                  <button
                    type="button"
                    aria-expanded={moNote === s.name}
                    aria-label={t('troLyAiKhoKhaiBang.moCanhBaoBang')}
                    title={t('troLyAiKhoKhaiBang.moCanhBaoBang')}
                    onClick={() => setMoNote((v) => (v === s.name ? null : s.name))}
                    className="shrink-0 px-1 py-2.5"
                  >
                    <Badge variant="warning">
                      <WarningOutlined className="me-0.5 text-2xs" aria-hidden />
                      {soVN(b.notes.length, locale)}
                    </Badge>
                  </button>
                ) : null}
                <button
                  type="button"
                  aria-expanded={moCot === s.name}
                  aria-label={t('troLyAiKhoKhaiBang.moChonCot')}
                  title={t('troLyAiKhoKhaiBang.moChonCot')}
                  onClick={() => setMoCot((v) => (v === s.name ? null : s.name))}
                  className="shrink-0 px-2 py-2.5"
                >
                  <Badge variant={daBo ? 'warning' : 'neutral'}>
                    {daBo
                      ? t('troLyAiKhoKhaiBang.soCotDaBo', { n: soVN(tongCot - daBo, locale), m: soVN(tongCot, locale) })
                      : t('troLyAiKhoKhaiBang.soCot', { n: soVN(tongCot, locale) })}
                  </Badge>
                </button>
              </div>
              {moNote === s.name ? (
                <ul className="space-y-1 border-t border-default px-3 py-2">
                  {b.notes.map((n) => (
                    <li key={n} className="text-xs leading-relaxed text-warning-700">
                      {n}
                    </li>
                  ))}
                </ul>
              ) : null}
              {moCot === s.name ? (
                <ChonCot
                  key={s.name}
                  suggestion={s}
                  bo={boCot[s.name] ?? new Set()}
                  onDoi={(moi) => setBoCot((cu) => ({ ...cu, [s.name]: moi }))}
                  onCopy={() => setCopyTu(s.name)}
                />
              ) : null}
            </div>
          );
        })}
        {loc.length > hien ? (
          <Button size="sm" variant="ghost" className="w-full" onClick={() => setHien((n) => n + MOI_LAN)}>
            {t('troLyAiKhoKhaiBang.hienThem', { n: soVN(loc.length - hien, locale) })}
          </Button>
        ) : null}
      </div>

      {/* Chứng từ thiếu bảng dòng — ngay TRÊN nút Lưu. Nhắc chứ KHÔNG tự tích: `ORDR` không kèm `RDR1` là lựa chọn hợp lệ. */}
      {thieuDong.length ? (
        <div className="flex flex-wrap items-start justify-between gap-2 rounded-lg bg-primary-50 px-3 py-2 text-xs text-primary-700">
          <span className="min-w-0">
            {t('troLyAiKhoKhaiBang.thieuBangDong', {
              ds: thieuDong
                .slice(0, 4)
                .map((x) => `${x.dau} → ${x.dong.join(', ')}`)
                .join(' · '),
              n: soVN(thieuDong.length, locale),
            })}
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setChon((cu) => new Set([...cu, ...thieuDong.flatMap((x) => x.dong)]))}
          >
            {t('troLyAiKhoKhaiBang.tichKemBangDong', {
              n: soVN(
                thieuDong.reduce((a, x) => a + x.dong.length, 0),
                locale,
              ),
            })}
          </Button>
        </div>
      ) : null}

      <div className="flex flex-wrap justify-end gap-2 border-t border-default pt-3">
        <Button variant="secondary" size="sm" onClick={onClose}>
          {t('troLyAiKhoKhaiBang.huy')}
        </Button>
        <Button size="sm" disabled={chon.size === 0} loading={luu.isPending} onClick={ghi}>
          {tien
            ? t('troLyAiKhoKhaiBang.dangLuu', { n: soVN(tien.xong, locale), m: soVN(tien.tong, locale) })
            : t('troLyAiKhoKhaiBang.khaiN', { n: soVN(chon.size, locale) })}
        </Button>
      </div>

      {copyTu && bangTheoTen[copyTu] ? (
        <CopyCotDialog
          nguon={bangTheoTen[copyTu].suggestion}
          boNguon={boCot[copyTu] ?? new Set()}
          bang={bang}
          onClose={() => setCopyTu(null)}
          onApDung={(dich) => apDungCot(copyTu, dich)}
        />
      ) : null}
    </div>
  );
}
